// src/pages/brands/Brands.jsx
import React, { useCallback, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Grid
} from '@mui/material';
import {
  Business as BusinessIcon,
  Edit as EditIcon,
  Analytics as AnalyticsIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  AutoAwesome as MagicIcon,
  Style as StyleIcon
} from '@mui/icons-material';

// Import components
import EntityListContainer from '../../components/common/EntityListContainer';
import { BrandCard } from '../../components/core/cards';
import EntityStatusBadge from '../../components/core/EntityStatusBadge';
import SecureAvatar from '../../components/ui/SecureAvatar';
import useConfirmDialog from '../../hooks/useConfirmationDialog';

// Import stores
import useBrandStore from '../../stores/brandStore';
import useNotificationStore from '../../stores/notificationStore';
import useAuthStore from '../../stores/authStore';

// Configuration
const FILTER_CONFIG = [
  {
    name: 'status',
    label: 'Status',
    allLabel: 'All Statuses',
    options: [
      { value: 'active', label: 'Active' },
      { value: 'inactive', label: 'Inactive' },
      { value: 'draft', label: 'Draft' }
    ]
  },
  {
    name: 'industry',
    label: 'Industry',
    allLabel: 'All Industries',
    options: [
      { value: 'Luxury', label: 'Luxury' },
      { value: 'Hospitality', label: 'Hospitality' },
      { value: 'Finance', label: 'Finance' },
      { value: 'Technology', label: 'Technology' },
      { value: 'E-commerce', label: 'E-commerce' },
      { value: 'Healthcare', label: 'Healthcare' },
      { value: 'Other', label: 'Other' }
    ]
  }
];

const SORT_OPTIONS = [
  { field: 'name', label: 'Name' },
  { field: 'createdAt', label: 'Date Created' },
  { field: 'industry', label: 'Industry' },
  { field: 'status', label: 'Status' }
];

/**
 * Brands listing page using EntityListContainer for consistent UI and behavior
 */
const Brands = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const getClientName = (client) => {
    if (!client) return 'N/A';
    if (typeof client === 'string') return client;
    return client.name || client.companyName || 'Unknown Client';
  };

  const getIndustryName = (industry) => {
    if (!industry) return 'N/A';
    if (typeof industry === 'string') return industry;
    return industry.primary || JSON.stringify(industry);
  };
  
  // Parse the client ID from query params if any
  const queryParams = new URLSearchParams(location.search);
  const clientId = queryParams.get('clientId');
  
  // Store hooks
  const { 
    brands, 
    meta, 
    isLoading, 
    criticalError, 
    fetchBrands,
    fetchClientBrands,
    deleteBrand 
  } = useBrandStore();
  
  const { showSuccess, showError } = useNotificationStore();
  const { hasPermission } = useAuthStore();
  
  // Confirmation dialog
  const [ConfirmDialog, confirm] = useConfirmDialog();
  
  // Check permissions
  const canCreate = hasPermission('brands', 'create');
  const canUpdate = hasPermission('brands', 'update');
  const canDelete = hasPermission('brands', 'delete');
  
  // Handle brand deletion with confirmation
  const handleDeleteBrand = useCallback(async (brand) => {
    const confirmed = await confirm({
      title: `Delete ${brand.name}?`,
      content: `This will permanently delete the brand and all associated data. This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      isDelete: true
    });
    
    if (confirmed) {
      try {
        await deleteBrand(brand._id);
        showSuccess(`Brand "${brand.name}" deleted successfully`);
      } catch (error) {
        showError(`Failed to delete brand: ${error.message}`);
      }
    }
  }, [confirm, deleteBrand, showSuccess, showError]);
  
  // Create fetchData function that handles different API calls based on presence of clientId
  const fetchData = useCallback((params) => {
    if (clientId) {
      return fetchClientBrands(clientId, params);
    } else {
      return fetchBrands(params);
    }
  }, [clientId, fetchBrands, fetchClientBrands]);
  
  // Entity renderer for grid view
  const renderBrand = useCallback((brand) => {
    // Safety check
    if (!brand || !brand._id) return null;
    
    return (
      <BrandCard 
        brand={brand}
        primaryAction={{
          label: 'View',
          icon: <ViewIcon />,
          onClick: () => navigate(`/brands/${brand._id}`),
          variant: 'contained',
          color: 'primary'
        }}
        onEdit={canUpdate ? () => navigate(`/brands/${brand._id}/edit`) : undefined}
        onDelete={canDelete ? () => handleDeleteBrand(brand) : undefined}
        onAnalytics={() => navigate(`/analytics/brands/${brand._id}`)}
        onManageAssets={() => navigate(`/brands/${brand._id}/style-guides`)}
        linkTo={`/brands/${brand._id}`}
      />
    );
  }, [navigate, canUpdate, canDelete, handleDeleteBrand]);

  // Define table columns
  const tableColumns = [
    { 
      id: 'name', 
      label: 'Brand Name',
      renderCell: (brand) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SecureAvatar
            src={brand?.logo?.primary}
            alt={brand.name}
            sx={{ width: 40, height: 40 }}
          >
            {brand.name?.charAt(0) || ''}
          </SecureAvatar>
          <Typography variant="body1" fontWeight={500}>
            {brand.name}
          </Typography>
          {brand.magicalElements?.enchantmentLevel && (
            <MagicIcon color="secondary" fontSize="small" sx={{ ml: 1 }} />
          )}
        </Box>
      )
    },
    { 
      id: 'status', 
      label: 'Status', 
      align: 'center',
      renderCell: (brand) => (
        <EntityStatusBadge
          entityType="brand"
          status={brand.status}
          size="small"
        />
      )
    },
    { 
      id: 'industry', 
      label: 'Industry',
      align: 'center',
      renderCell: (brand) => (
        <Chip
          label={getIndustryName(brand.industry)}
          size="small"
          variant="outlined"
        />
      )
    },
    { 
      id: 'client', 
      label: 'Client',
      align: 'center',
      renderCell: (brand) => (
        <Typography variant="body2">
          {getClientName(brand.client)}
        </Typography>
      )
    },
    { 
      id: 'assets', 
      label: 'Assets',
      align: 'center',
      renderCell: (brand) => {
        // Calculate counts safely
        const styleGuideCount = brand.styleGuides?.length || 0;
        const assetsCount = brand.assets?.length || 0;
        
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">Style Guides</Typography>
              <Typography variant="body1">{styleGuideCount}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">Assets</Typography>
              <Typography variant="body1">{assetsCount}</Typography>
            </Box>
          </Box>
        );
      }
    }
  ];

  // Configure row actions for table view
  const getTableRowActions = useCallback((brand) => {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Tooltip title="View Style Guides">
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/brands/${brand._id}/style-guides`);
            }}
          >
            <StyleIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Analytics">
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/analytics/brands/${brand._id}`);
            }}
          >
            <AnalyticsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        {canUpdate && (
          <Tooltip title="Edit">
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/brands/${brand._id}/edit`);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        
        {canDelete && (
          <Tooltip title="Delete">
            <IconButton 
              size="small" 
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteBrand(brand);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }, [canUpdate, canDelete, navigate, handleDeleteBrand]);

  return (
    <>
      <EntityListContainer
        title={clientId ? 'Client Brands' : 'Brands'}
        description="Manage brand identities, assets, and guidelines"
        fetchData={fetchData}
        renderEntity={renderBrand}
        initialFilters={{
          status: 'all',
          industry: 'all',
        }}
        initialSort={{ field: 'name', direction: 'asc' }}
        filterConfig={FILTER_CONFIG}
        sortOptions={SORT_OPTIONS}
        
        // View options
        availableViews={['grid', 'table']}
        defaultView="grid"
        
        // Table configuration
        tableConfig={{
          columns: tableColumns,
          onRowClick: (brand) => navigate(`/brands/${brand._id}`),
          renderRowActions: getTableRowActions
        }}
        
        // Use direct store values
        entities={brands}
        isLoading={isLoading}
        error={null}  // Don't show error - let empty state handle it
        metadata={meta}
        
        actions={{
          canCreate,
          createPath: clientId ? `/brands/create?clientId=${clientId}` : '/brands/create',
          createLabel: 'Create Brand'
        }}
        emptyState={{
          title: 'No Brands Yet',
          description: clientId 
            ? "Get started by adding your first brand to this client." 
            : "Get started by adding your first brand to manage their identity and content.",
          icon: <BusinessIcon sx={{ fontSize: 64 }} />,
          getFilteredTitle: () => 'No Matching Brands',
          getFilteredDescription: () => 'Try adjusting your search or filters to find what you\'re looking for.'
        }}
      />
      
      <ConfirmDialog />
    </>
  );
};

export default Brands;